import { InputAdornment, List, TextField } from "@mui/material";
import React, { forwardRef, useRef } from "react";
import { toast } from "react-toastify";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import UploaderInput from "./UploaderInput";
import Sortable from "./Sortable";
import useTranslation from "next-translate/useTranslation";
import { generateUniqueId, Text } from "@utils/helper";
import cloneDeep from "lodash.clonedeep";

const FileUploader = forwardRef(
  (
    {
      name,
      files,
      errors,
      onChange,
      onDeleteUploaded,
      label,
      max,
      clsName,
      single = false,
      variant = "input",
      disabled,
      helperText,
      customLabel,
      classNameLabel,
      otherSx,
      additionalStyles,
      error,
      wrapperClasses,
      acceptableFileTypes = ["jpg", "jpeg", "pdf", "png"],
    },
    ref
  ) => {
    const fileRef = useRef(ref);
    const { t, lang } = useTranslation();
    const isRtl = lang === "ar";

    const labelClickHandler = () => {
      if (!disabled) {
        fileRef.current?.click();
      }
    };

    const fileValidation = (file) => {
      const fileExtension = (file?.name || "").split(".").pop().toLowerCase();
      return acceptableFileTypes.includes(fileExtension);
    };

    const filesChangeHandler = (e) => {
      const selectedFiles = e.target?.files;

      if (!(selectedFiles instanceof FileList) || !selectedFiles.length) {
        return;
      } else if (single) {
        const fileSize = selectedFiles[0]?.size / 1024 / 1024;
        const fileType = selectedFiles[0].type;
        const file = selectedFiles[0];
        if (fileSize > max) {
          toast.error(
            t("validation.validationAttributes.images.limit", {
              size: max,
              currentSize: fileSize.toFixed(2),
            })
          );
          return;
        } else if (!fileValidation(file)) {
          toast.error(
            t("validation.validationAttributes.images.type", {
              types: acceptableFileTypes.join(" - "),
              currentType: fileType,
            })
          );
          return;
        }

        if (files.length && !(files[0] instanceof File) && onDeleteUploaded) {
          onDeleteUploaded(files[0].id).then(() => {
            onChange([selectedFiles[0]]);
          });
        } else {
          onChange([selectedFiles[0]]);
        }
      } else {
        const clonedFiles = cloneDeep(files);

        for (let itemIndex = 0; itemIndex < selectedFiles.length; itemIndex++) {
          const file = selectedFiles.item(itemIndex);
          const fileSize = file.size / 1024 / 1024;
          const fileType = file.type;
          if (fileSize > max) {
            toast.error(
              t("validation.validationAttributes.images.limit", {
                size: max,
                currentSize: fileSize.toFixed(2),
              })
            );
            return;
          } else if (!fileValidation(file)) {
            toast.error(
              t("validation.validationAttributes.images.type", {
                types: acceptableFileTypes.join(),
                currentType: fileType,
              })
            );
            return;
          }
          clonedFiles.push({
            name: file.name,
            file,
            id: generateUniqueId(),
          });
        }
        onChange(clonedFiles);
      }
    };

    const deleteFileHandler = (key) => {
      const file = files[key];

      if (!file) {
        return;
      }

      const clonedFiles = cloneDeep(files);

      if (file.file instanceof File || !onDeleteUploaded) {
        clonedFiles.splice(key, 1);
        onChange(clonedFiles);
        return (fileRef.current.value = clonedFiles);
      }

      onDeleteUploaded(file.id, key).then(() => {
        clonedFiles.splice(key, 1);
        onChange(clonedFiles);
        return (fileRef.current.value = clonedFiles);
      });
    };

    return (
      <div className={wrapperClasses}>
        {variant === "input" ? (
          <>
            <input
              onChange={filesChangeHandler}
              ref={fileRef}
              type="file"
              multiple={!single}
              hidden
            />
            <TextField
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachFileRoundedIcon />
                  </InputAdornment>
                ),
              }}
              error={error}
              helperText={helperText}
              onClick={labelClickHandler}
              placeholder={label}
              name={name}
              variant="outlined"
              fullWidth
            />
          </>
        ) : (
          <div className={`flex flex-col gap-4 w-full ${clsName}`}>
            {customLabel ? (
              React.isValidElement(customLabel) ? (
                customLabel
              ) : (
                <Text fontType="Expo_sim" className={classNameLabel}>
                  {customLabel}
                </Text>
              )
            ) : null}
            <UploaderInput
              ref={fileRef}
              error={error}
              helperText={helperText}
              disabled={disabled}
              label={label}
              onChange={filesChangeHandler}
              name={name}
              single={single}
              otherSx={otherSx}
              additionalStyles={additionalStyles}
            />
          </div>
        )}
        {files?.length ? (
          <List>
            <Sortable
              disabled={disabled}
              itemsList={files}
              errors={errors}
              onDelete={deleteFileHandler}
              onSort={onChange}
              isRtl={isRtl}
            />
          </List>
        ) : null}
      </div>
    );
  }
);

FileUploader.displayName = "FileUploader";

export default FileUploader;
